import { z } from 'zod';
import { BooleanQuery, Dateish, PaginationQuerySchema } from './common';

/** 'client' (v4) = usuário do portal do cliente; nunca acessa a API staff. */
export const UserRoleSchema = z.enum(['admin', 'attendant', 'client']);
export type UserRole = z.infer<typeof UserRoleSchema>;

/**
 * Papel a partir das roles do realm do Keycloak. Regra ÚNICA das duas pontas:
 * a API a usa para autorizar a requisição e o SPA para decidir qual aplicação
 * renderizar (portal × equipe).
 *
 * `client` decide PRIMEIRO e de forma EXCLUSIVA: uma role a mais no token
 * nunca vira escalada. Quem tiver 'client' junto de 'admin' PERDE acesso, não
 * ganha — o conserto é tirar a role indevida no Keycloak, e até lá a pessoa vê
 * apenas o portal.
 *
 * Por que compartilhada: em 2026-08-24 o SPA tinha a sua própria versão, que
 * esquecia 'client' e devolvia 'attendant'. O cliente entrava e recebia a
 * casca da equipe — a API recusava cada chamada com 403, então nada vazou, mas
 * a tela vinha cheia de erro e passava exatamente a impressão contrária.
 */
export function resolveRoleFromRealmRoles(roles: readonly unknown[]): UserRole {
  if (roles.includes('client')) return 'client';
  if (roles.includes('admin')) return 'admin';
  return 'attendant';
}

export const UserSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  role: UserRoleSchema,
  /**
   * Nas leituras de /users: ativo NESTE tenant — a conta ligada e o vínculo com
   * a empresa da requisição ligado (spec 042). Desligar alguém na PoolTrack não
   * o mostra inativo na Fatorh.
   */
  active: z.boolean(),
  createdAt: Dateish,
});
export type User = z.infer<typeof UserSchema>;

/**
 * As abas da tela de usuários (spec 034): `team` = a equipe deste tenant (o
 * predicado `tenantTeam` da API, o mesmo do seletor de responsável); `portal` =
 * os logins de portal das associações (role `client`). Sem `kind`, todos —
 * e todos = equipe + portal, porque o papel só tem esses três valores.
 */
export const UserKindSchema = z.enum(['team', 'portal']);
export type UserKind = z.infer<typeof UserKindSchema>;

/**
 * `GET /users` — as pessoas DESTE tenant, equipe e logins de portal (F-108):
 * a tela de usuários e o contador "Usuários" das Configurações. A lista é
 * paginada no servidor (`limit`/`offset`, o total em `X-Total-Items`), como a
 * de clientes: até F-108 ela era a tabela global inteira, cortada em 200 por
 * nome, e a PoolTrack sozinha tem mais de 270 pessoas.
 *
 * `q` busca nome ou e-mail sem acento e sem caixa; `active` filtra; `kind`
 * recorta equipe ou portal (spec 034) — no servidor, porque a lista é paginada
 * e um filtro na tela mostraria só o recorte da página.
 */
export const UsersListQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
  active: BooleanQuery.optional(),
  kind: UserKindSchema.optional(),
});
export type UsersListQuery = z.infer<typeof UsersListQuerySchema>;

/** `GET /users/counts` — os mesmos `q`/`active` da lista, sem `kind` (spec 034). */
export const UsersCountsQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  active: BooleanQuery.optional(),
});
export type UsersCountsQuery = z.infer<typeof UsersCountsQuerySchema>;

/** Quantas pessoas cada aba da tela de usuários teria — `all` = `team` + `portal`. */
export const UsersCountsSchema = z.object({
  all: z.number().int(),
  team: z.number().int(),
  portal: z.number().int(),
});
export type UsersCounts = z.infer<typeof UsersCountsSchema>;

/**
 * `POST /users/:id/password-reset` (spec 034) — a senha que o servidor gerou,
 * devolvida UMA vez (`Cache-Control: no-store`) e nunca mais: nenhuma rota a lê
 * de volta, o rastro não a guarda, e a tela a descarta ao fechar.
 *
 * - `mustChangeOnLogin`: true no modo keycloak (a credencial é temporária e o
 *   Keycloak obriga a pessoa a criar a senha dela no primeiro acesso); false no
 *   modo local, que não tem essa ação obrigatória;
 * - `loginUrl`: onde a pessoa entra (WEB_PUBLIC_URL deste ambiente) — vai na
 *   mensagem pronta para colar no WhatsApp.
 */
export const PasswordResetResponseSchema = z.object({
  userId: z.number().int(),
  name: z.string(),
  email: z.string(),
  temporaryPassword: z.string(),
  mustChangeOnLogin: z.boolean(),
  loginUrl: z.string(),
});
export type PasswordResetResponse = z.infer<typeof PasswordResetResponseSchema>;

/** quantas pessoas a tela de usuários mostra por página */
export const USERS_PAGE_SIZE = 50;

export const LoginBodySchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

export const LoginResponseSchema = z.object({
  token: z.string(),
  user: UserSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** `POST /users` em AUTH_MODE=local: a senha é nossa (hash em users.password_hash). */
export const UserCreateBodySchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  role: UserRoleSchema.default('attendant'),
  password: z.string().min(6),
});
export type UserCreateBody = z.infer<typeof UserCreateBodySchema>;

/**
 * O MESMO endpoint em AUTH_MODE=keycloak (F-174). A conta é criada no realm,
 * ligada e SEM senha: a primeira é a do "Redefinir senha" (temporária, mostrada
 * uma vez ao admin). Por isso `password` é PROIBIDO (`.strict()` → 422), como no
 * login de portal (ClientUserKeycloakCreateBodySchema) e no `PATCH`: aceitar e
 * não guardar em lugar nenhum seria responder 201 a uma senha que não existe.
 */
export const UserKeycloakCreateBodySchema = z
  .object({
    name: z.string().min(1),
    email: z.string().email(),
    role: UserRoleSchema.default('attendant'),
  })
  .strict();
export type UserKeycloakCreateBody = z.infer<typeof UserKeycloakCreateBodySchema>;

/**
 * Resposta do `POST /users`: a pessoa e se ela foi ADOTADA (F-180). `adopted`
 * true (200): o e-mail já era de alguém do ERP sem acesso a esta empresa — a
 * equipe que o espelho do TiFlux trouxe, por exemplo —, e a linha dela (o id,
 * os tickets, o nome) foi mantida; o que nasceu foi o acesso a esta empresa, com
 * o papel escolhido, e o login no SSO. false (201): a pessoa nasceu agora.
 */
export const UserCreateResponseSchema = UserSchema.extend({
  adopted: z.boolean(),
});
export type UserCreateResponse = z.infer<typeof UserCreateResponseSchema>;

/**
 * `PATCH /users/:id`. `active` liga/desliga o acesso da pessoa A ESTE tenant
 * (spec 042) — a conta inteira só acompanha quando não sobra empresa. Desligar
 * a si mesmo é 409 `deactivate_self`.
 */
export const UserPatchBodySchema = z
  .object({
    name: z.string().min(1),
    email: z.string().email(),
    role: UserRoleSchema,
    active: z.boolean(),
    password: z.string().min(6),
  })
  .partial()
  .strict();
export type UserPatchBody = z.infer<typeof UserPatchBodySchema>;

/**
 * O que a mudança de "Ativo" fez com a CONTA de login (spec 042), além deste
 * tenant: `unchanged` (a pessoa ainda entra noutra empresa, ou já estava
 * assim); `disabled`/`enabled` (era o último vínculo: a conta inteira
 * desligou — e as sessões caíram — ou religou); `disable_failed`/`enable_failed`
 * (o SSO não respondeu ou recusou: o vínculo deste tenant já vale, e salvar de
 * novo tenta a conta outra vez).
 */
export const AccountChangeSchema = z.enum([
  'unchanged',
  'disabled',
  'enabled',
  'disable_failed',
  'enable_failed',
]);
export type AccountChange = z.infer<typeof AccountChangeSchema>;

/** Resposta do `PATCH /users/:id`: a pessoa e, quando `active` veio, o efeito na conta. */
export const UserPatchResponseSchema = UserSchema.extend({
  account: AccountChangeSchema.optional(),
});
export type UserPatchResponse = z.infer<typeof UserPatchResponseSchema>;
